import {
  useGetDemoQuery,
  useGetDemoSearchLinksResultsQuery,
  useGetPresentationsByDemoQuery,
} from '@carboncopyco/data';
import {
  BrowserIcon,
  CanvasGraphicsIcon,
  DurationOutlineIcon,
  LinkIcon,
  TrendingOutlineIcon,
} from '@carboncopyco/icons';
import { useBoolean } from 'ahooks';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { DiscardChangesModal, useSpeakerNotes } from '../../SpeakerNotes';
import { useLinksPermissions } from '../../utils/hooks/useLinksPermissions';
import { InfoPageSidebar, type TabButtons } from '../InfoPageSidebar';
import { SpeakerNotesIcon } from '../SpeakerNotesIcon';
import { useDemoPageProps } from './DemoPageContext';
import type { DemoPageTab } from './types';

export const DemoPageSidebar = () => {
  const [showDiscardModal, setShowDiscardModal] = useBoolean();
  const {
    goBack,
    demoId,
    organizationId,
    rootPath,
    noteValue,
    setNoteValue,
    pendingNavigationRoute,
    setPendingNavigationRoute,
    workspaceId,
  } = useDemoPageProps();
  const { data: demo, isLoading: isLoadingDemo } = useGetDemoQuery({
    organizationId,
    workspaceId,
    id: demoId,
  });
  const { getIsDemoShareable } = useLinksPermissions();
  const { data: linksResponse, isLoading: isLoadingShares } =
    useGetDemoSearchLinksResultsQuery({
      organizationId,
      workspaces: [workspaceId],
      id: demoId,
      active: true,
    });
  const { data: presentationsResponse, isLoading: isLoadingPresentations } =
    useGetPresentationsByDemoQuery({
      organizationId,
      workspaceId,
      demoId,
    });

  const flags = useFlags();
  const tabButtons = useMemo<TabButtons<DemoPageTab>>(() => {
    const buttons = {
      overview: {
        label: 'Overview',
        icon: <BrowserIcon size={18} />,
        disabled: false,
      },
      presentations: {
        label:
          isLoadingPresentations || !presentationsResponse?.metadata.totalItems
            ? 'Presentations'
            : `Presentations (${presentationsResponse?.metadata.totalItems})`,
        icon: <CanvasGraphicsIcon size={18} />,
        disabled: false,
      },
      ...(getIsDemoShareable(demo) && {
        links: {
          label:
            isLoadingShares || !linksResponse?.metadata.totalItems
              ? 'Shares'
              : `Shares (${linksResponse?.metadata.totalItems})`,
          icon: <LinkIcon size={18} />,
          disabled: false,
        },
      }),
      activity: {
        label: 'Activity',
        icon: <TrendingOutlineIcon size={18} />,
        disabled: false,
      },
      history: {
        label: 'History',
        icon: <DurationOutlineIcon size={18} />,
        disabled: false,
      },
    };

    return {
      ...buttons,
      ...(flags.speakerNotes
        ? {
            'speaker-notes': {
              label: 'Speaker notes',
              icon: (
                <SpeakerNotesIcon
                  organizationId={organizationId}
                  entityType="demo"
                  entityId={demoId}
                />
              ),
            },
          }
        : {}),
    };
  }, [
    demoId,
    organizationId,
    flags,
    demo,
    getIsDemoShareable,
    isLoadingPresentations,
    isLoadingShares,
    linksResponse?.metadata.totalItems,
    presentationsResponse?.metadata.totalItems,
  ]);
  const navigate = useNavigate();
  const { note: savedNote } = useSpeakerNotes({
    entityType: 'demo',
    entityId: demoId,
  });
  const { tabId: currentTabId } = useParams();
  const hasUnsavedChanges = savedNote
    ? savedNote.content !== noteValue
    : !!noteValue;
  const handleChange = (tabId: string) => {
    const route = `${rootPath}/${tabId}`;
    if (hasUnsavedChanges) {
      setPendingNavigationRoute(route);
      setShowDiscardModal.setTrue();
      return;
    }
    navigate(route);
  };

  return (
    <>
      <InfoPageSidebar<DemoPageTab>
        handleChange={
          currentTabId === 'speaker-notes' ? handleChange : undefined
        }
        tabButtons={tabButtons}
        screenshot={demo?.image}
        rootPath={rootPath}
        goBack={goBack}
        isLoading={isLoadingDemo}
      />
      {showDiscardModal && (
        <DiscardChangesModal
          onDiscard={() => {
            setNoteValue(savedNote?.content);
            setShowDiscardModal.setFalse();
            navigate(pendingNavigationRoute);
          }}
          onCancel={setShowDiscardModal.setFalse}
        />
      )}
    </>
  );
};
